import type { GeospatialPoint } from "./calculate-geospatial"

export function convertToDms(dd: number, isLng: boolean, unrounded?: boolean) {
    const dir = dd < 0 ? (isLng ? "W" : "S") : isLng ? "E" : "N"

    const absDd = Math.abs(dd)
    const deg = absDd | 0
    const frac = absDd - deg
    const min = (frac * 60) | 0
    const sec = unrounded ? (frac * 3600 - min * 60).toFixed(4) : Math.round(frac * 3600 - min * 60)

    return deg + " " + min + " " + sec + " " + dir
}

export function convertToFormattedDms(dd: number, isLng: boolean, unrounded?: boolean) {
    const dir = dd < 0 ? (isLng ? "W" : "S") : isLng ? "E" : "N"

    const absDd = Math.abs(dd)
    const deg = absDd | 0
    const frac = absDd - deg
    const min = (frac * 60) | 0
    const sec = unrounded ? (frac * 3600 - min * 60).toFixed(4) : Math.round(frac * 3600 - min * 60)

    return `${deg}\u00B0 ${min}\u2032 ${sec}" ${dir}`
}
export const convertDMS = (lng: number, lat: number) => {
    const latitude = convertToDms(lat, false)
    const longitude = convertToDms(lng, true)

    return [latitude, longitude]
}

export const convertDMSUnrounded = (lng: number, lat: number) => {
    const latitude = convertToDms(lat, false, true)
    const longitude = convertToDms(lng, true, true)

    return [latitude, longitude]
}

function convertToDm(dd: number, isLng: boolean, fixed: number) {
    const dir = dd < 0 ? (isLng ? "W" : "S") : isLng ? "E" : "N"

    const absDd = Math.abs(dd)
    const deg = absDd | 0
    const frac = absDd - deg
    const min = frac * 60
    return deg + " " + min.toFixed(fixed) + " " + dir
}

export const convertDM = (lng: number, lat: number, fixedX: number, fixedY: number) => {
    const longitude = convertToDm(lng, true, fixedX)
    const latitude = convertToDm(lat, false, fixedY)

    return [latitude, longitude]
}

export const convertToFormattedDm = (dd: number, isLng: boolean, unrounded?: boolean) => {
    const dir = dd < 0 ? (isLng ? "W" : "S") : isLng ? "E" : "N"

    const absDd = Math.abs(dd)
    const deg = absDd | 0
    const frac = absDd - deg
    const min = frac * 60
    return `${deg}\u00B0 ${unrounded ? min : min.toFixed(4)}\u2032 ${dir}`
}

type CoordinateAxis = "latitude" | "longitude"

const HEMISPHERES = {
    n: { axis: "latitude", sign: 1 },
    s: { axis: "latitude", sign: -1 },
    e: { axis: "longitude", sign: 1 },
    w: { axis: "longitude", sign: -1 },
} satisfies Record<string, { axis: CoordinateAxis; sign: number }>

type Hemisphere = keyof typeof HEMISPHERES

const SEXAGESIMAL_UNITS = ["degrees", "minutes", "seconds"] as const
type SexagesimalUnit = (typeof SEXAGESIMAL_UNITS)[number]

// longest symbol first, so a doubled prime reads as seconds rather than two minutes
const UNIT_SYMBOLS: [string, SexagesimalUnit][] = [
    ["deg", "degrees"],
    ["°", "degrees"],
    ["º", "degrees"],
    ["''", "seconds"],
    ["′′", "seconds"],
    ['"', "seconds"],
    ["″", "seconds"],
    ["”", "seconds"],
    ["'", "minutes"],
    ["′", "minutes"],
    ["’", "minutes"],
]

type CoordinateToken =
    | { kind: "number"; value: number }
    | { kind: "unit"; unit: SexagesimalUnit }
    | { kind: "hemisphere"; hemisphere: Hemisphere }
    | { kind: "negative" }
    | { kind: "separator" }

const isDigit = (character: string) => character >= "0" && character <= "9"

const isHemisphere = (character: string): character is Hemisphere => character in HEMISPHERES

const readNumber = (input: string, start: number) => {
    let end = start
    while (isDigit(input[end])) end++
    if (input[end] === ".") {
        end++
        while (isDigit(input[end])) end++
    }
    return { value: Number(input.slice(start, end)), end }
}

const tokenizeCoordinate = (input: string) => {
    const tokens: CoordinateToken[] = []
    let cursor = 0

    while (cursor < input.length) {
        const character = input[cursor]

        if (!character.trim()) {
            cursor++
        } else if (character === ",") {
            tokens.push({ kind: "separator" })
            cursor++
        } else if (character === "-") {
            tokens.push({ kind: "negative" })
            cursor++
        } else if (isDigit(character)) {
            const { value, end } = readNumber(input, cursor)
            tokens.push({ kind: "number", value })
            cursor = end
        } else if (isHemisphere(character)) {
            tokens.push({ kind: "hemisphere", hemisphere: character })
            cursor++
        } else {
            const symbol = UNIT_SYMBOLS.find(([text]) => input.startsWith(text, cursor))
            if (!symbol) return
            tokens.push({ kind: "unit", unit: symbol[1] })
            cursor += symbol[0].length
        }
    }

    return tokens
}

const splitOnSeparators = (tokens: CoordinateToken[]) =>
    tokens.reduce<CoordinateToken[][]>(
        (components, token) => {
            if (token.kind === "separator") components.push([])
            else components[components.length - 1].push(token)
            return components
        },
        [[]]
    )

const splitOnHemispheres = (tokens: CoordinateToken[]) => {
    const components: CoordinateToken[][] = []
    let current: CoordinateToken[] = []

    for (const token of tokens) {
        if (token.kind === "hemisphere" && current[0]?.kind === "hemisphere") {
            components.push(current)
            current = []
        }
        const leads = current.length === 0
        current.push(token)
        if (token.kind === "hemisphere" && !leads) {
            components.push(current)
            current = []
        }
    }
    if (current.length) components.push(current)

    return components
}

const splitEvenlyByNumbers = (tokens: CoordinateToken[]) => {
    const numbers = tokens.flatMap((token, index) => (token.kind === "number" ? index : []))
    if (numbers.length < 2 || numbers.length % 2 !== 0) return

    const middle = numbers[numbers.length / 2]
    const cut = tokens[middle - 1]?.kind === "negative" ? middle - 1 : middle
    return [tokens.slice(0, cut), tokens.slice(cut)]
}

const splitCoordinateComponents = (tokens: CoordinateToken[]) => {
    const separated = splitOnSeparators(tokens)
    if (separated.length > 1) return separated

    const byHemisphere = splitOnHemispheres(tokens)
    if (byHemisphere.length > 1) return byHemisphere

    return splitEvenlyByNumbers(tokens)
}

const readHemisphere = (token?: CoordinateToken) =>
    token?.kind === "hemisphere" ? token.hemisphere : undefined

const parseCoordinateComponent = (tokens: CoordinateToken[]) => {
    const prefix = readHemisphere(tokens[0])
    let cursor = prefix ? 1 : 0

    const negative = tokens[cursor]?.kind === "negative"
    if (negative) cursor++

    const values: number[] = []
    while (values.length < SEXAGESIMAL_UNITS.length) {
        const number = tokens[cursor]
        if (number?.kind !== "number") break
        cursor++

        const unit = tokens[cursor]
        if (unit?.kind === "unit") {
            if (unit.unit !== SEXAGESIMAL_UNITS[values.length]) return
            cursor++
        }
        values.push(number.value)
    }

    const suffix = readHemisphere(tokens[cursor])
    if (suffix) cursor++
    if (cursor !== tokens.length || !values.length || (prefix && suffix)) return

    const hemisphere = prefix ?? suffix
    if (hemisphere && negative) return

    const [degrees, minutes = 0, seconds = 0] = values
    if (values.length > 1 && !Number.isInteger(degrees)) return
    if (values.length > 2 && !Number.isInteger(minutes)) return
    if (minutes >= 60 || seconds >= 60) return

    const { axis, sign } = hemisphere ? HEMISPHERES[hemisphere] : { axis: undefined, sign: 1 }
    const magnitude = degrees + minutes / 60 + seconds / 3600

    return { value: magnitude * (negative ? -1 : sign), axis }
}

export const parseCoordinate = (input: string) => {
    const tokens = tokenizeCoordinate(input.toLowerCase())
    if (!tokens) return

    const components = splitCoordinateComponents(tokens)
    if (components?.length !== 2) return

    const first = parseCoordinateComponent(components[0])
    const second = parseCoordinateComponent(components[1])
    if (!first || !second || (first.axis && first.axis === second.axis)) return

    const isLatitudeFirst = first.axis !== "longitude" && second.axis !== "latitude"
    const latitude = isLatitudeFirst ? first.value : second.value
    const longitude = isLatitudeFirst ? second.value : first.value
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return

    return { latitude, longitude } satisfies GeospatialPoint
}
